import React, { useState, useCallback, useEffect } from 'react'
import { CircularProgress, FormControl, Grid, Typography } from '@material-ui/core'
import IndustryDefinitionComponent from './industryDefinitions'
import { StyledButton, StyledMenuItem } from '../../../theme/Global'
import { FilledInputStyle, isNumber, LabelStyle, NumberFormatCustom, SmartHelper, StyledSwitch } from '../../../theme/Forms'
import { StyledInlineSelect } from '../../../theme/Table'
import SettingsPopover from './components/Popover'
import {
  InputAdornment,
  menuPositionProps,
  paddedBarWrapper,
  SettingsHeadingStyle,
  staticTypography,
  titleTypography
} from './styles'
import { SettingsConfirmationModal } from './components/ConfirmationModal'

const SettingsHeading = ({ children }) => <SettingsHeadingStyle variant="h4" align="left">{children}</SettingsHeadingStyle>
const SettingsHeadline = ({ children }) => <Typography variant="h5" align="left" style={titleTypography}>{children}</Typography>
const SettingsTitle = ({ children }) => <Typography variant="h6" paragraph={false} style={staticTypography}>{children}</Typography>

const CompanySettingsRenderer = ({
  corpName,
  industryOptions,
  companyProps,
  save,
  userName,
  loading
}) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false)
  const [company, setCompany] = useState({
    annualRevenue: 0,
    employeeCount: 0,
    hasTippedEmployees: false,
    industry: { name: 'Standard' },
    ...companyProps
  })

  const closeConfirmationModal = () => {
    setConfirmationModalOpen(false)
  }

  const handleOnChange = useCallback((attribute, value) => {
    if (attribute === 'industry') {
      const industry = { name: value }
      setCompany({ ...company, industry })
      return
    }
    if (attribute === 'subIndustry') {
      const industry = { name: company.industry.name, subIndustry: { name: value } }
      setCompany({ ...company, industry })
      return
    }
    if (attribute === 'subSubIndustry') {
      const industry = { name: company.industry.name, subIndustry: { name: company.industry.subIndustry.name, subIndustry: { name: value } } }
      setCompany({ ...company, industry })
      return
    }
    setCompany({
      ...company,
      [attribute]: value
    })
  })

  const saveCompanySettings = async () => {
    setConfirmationModalOpen(false)
    save(company)
  }

  useEffect(() => {
    // HACK: override subindustry and subsubindustry based on these conditions
    const isCompanyIndustryHospitality = company.industry === 'Hospitality'
    const isNonFastFood = company.subIndustry === 'Non-Fast Food'

    if (isCompanyIndustryHospitality) {
      if (!isNonFastFood) {
        setCompany({
          ...company,
          subSubIndustry: undefined
        })
      }
    } else {
      setCompany({
        ...company,
        subIndustry: undefined,
        subSubIndustry: undefined
      })
    }
  }, [company.industry, company.subIndustry])

  useEffect(() => {
    const isTippedEmployeesNull = company.hasTippedEmployees === null

    if (isTippedEmployeesNull) {
      setCompany({
        ...company,
        hasTippedEmployees: false
      })
    }
  }, [company.hasTippedEmployees])

  return (
    <Grid container spacing={8}>
      <SettingsConfirmationModal
        {...{
          open: confirmationModalOpen,
          close: closeConfirmationModal,
          save: saveCompanySettings
        }}
      />
      <Grid item xs={12} style={{ marginTop: '4rem' }}>
        <SettingsHeading>
          Company Settings
          <SettingsPopover>
            Your Company Settings are initially set by your Account Manager,
            based on data at the time of implementation.
            If this information has changed you may adjust it below.
            Please note that changes made here will affect the information
            shown for your locations.
            If you have questions, please contact your GovDocs Account Manager.
          </SettingsPopover>
        </SettingsHeading>
      </Grid>
      <Grid container spacing={8}>
        <Grid item xs={12}>
          <SettingsHeadline>Name: </SettingsHeadline>
          <SettingsTitle>{userName}</SettingsTitle>
          <br />
          <SettingsHeadline>Company: </SettingsHeadline>
          <SettingsTitle>{corpName}</SettingsTitle>
        </Grid>
      </Grid>
      <Grid style={{ marginTop: '2rem' }} container spacing={8}>
        <Grid container spacing={24} alignContent="flex-start">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <LabelStyle for="totalEmployees">Total Employees</LabelStyle>
              <FilledInputStyle
                style={{ marginBottom: '10px' }}
                helperText={<SmartHelper>Enter the total number of employees within your company.</SmartHelper>}
                name="totalEmployees"
                disabled={loading}
                onChange={(event) => handleOnChange('employeeCount', event.target.value)}
                type="text"
                variant="filled"
                error={!isNumber(company.employeeCount)}
                value={company.employeeCount}
                InputProps={{ inputComponent: NumberFormatCustom }}
              />
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <LabelStyle for="annualRevenue">Approximate Annual Revenue</LabelStyle>
              <FilledInputStyle
                style={{ marginBottom: '10px' }}
                helperText={<SmartHelper>Enter the approximate annual revenue for your company.</SmartHelper>}
                name="annualRevenue"
                disabled={loading}
                type=""
                variant="filled"
                onChange={(event) => handleOnChange('annualRevenue', event.target.value)}
                error={!isNumber(company.annualRevenue)}
                InputProps={{
                  startAdornment: <InputAdornment>$</InputAdornment>,
                  inputComponent: NumberFormatCustom
                }}
                value={company.annualRevenue}
              />
            </FormControl>
          </Grid>
        </Grid>
        <Grid container spacing={24} alignContent="flex-start">
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <LabelStyle for="industry">Industry <IndustryDefinitionComponent /></LabelStyle>
              <StyledInlineSelect
                style={{ marginBottom: '10px' }}
                disabled={loading}
                name="industry"
                MenuProps={menuPositionProps}
                value={company.industry.name}
                onChange={(event) => handleOnChange('industry', event.target.value)}
                autoWidth={false}
              >
                {industryOptions.companyIndustry.list.map((topLevelIndustry, idx) => (
                  <StyledMenuItem value={topLevelIndustry.name} key={`${topLevelIndustry.name}_${idx}`}>
                    {topLevelIndustry.name}
                  </StyledMenuItem>
                ))}
              </StyledInlineSelect>
            </FormControl>
          </Grid>

          {(company.industry.name === 'Hospitality') ? (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <LabelStyle for="subIndustry">
                  Is your company considered to be in one of the following {company.industry.name} industries?
                </LabelStyle>
                {industryOptions ? (
                  <StyledInlineSelect
                    style={{ marginBottom: '10px' }}
                    disabled={loading}
                    name="subIndustry"
                    MenuProps={menuPositionProps}
                    value={(company.industry.subIndustry && company.industry.subIndustry.name) || ''}
                    onChange={(event) => handleOnChange('subIndustry', event.target.value)}
                    autoWidth={false}
                  >
                    {industryOptions.companySubIndustry.list.map((companySubIndustry, idx) => (
                      <StyledMenuItem
                        value={companySubIndustry.name}
                        key={`${companySubIndustry.name}_${idx}`}
                      >
                        {companySubIndustry.name}
                      </StyledMenuItem>
                    ))}
                  </StyledInlineSelect>
                ) : null}
              </FormControl>
            </Grid>
          ) : null}
        </Grid>
        <Grid style={{ marginTop: '0.5rem' }} container spacing={24} alignContent="flex-start">
          {/* Keeping this grid item here to keep subsubIndustry below subIndustry */}
          <Grid item xs={12} sm={6} />
          {(company.industry.name === 'Hospitality' && company.industry.subIndustry && company.industry.subIndustry.name === 'Non-Fast Food') ? (
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <LabelStyle for="subsubIndustry">
                  Is your company primarily concerned with one of the following {company.subIndustry}{' '}
                  employee types?
                </LabelStyle>
                {industryOptions ? (
                  <StyledInlineSelect
                    style={{ marginBottom: '10px' }}
                    disabled={loading}
                    name="subsubIndustry"
                    MenuProps={menuPositionProps}
                    value={(company.industry.subIndustry && company.industry.subIndustry.subIndustry && company.industry.subIndustry.subIndustry.name) || ''}
                    onChange={(event) => handleOnChange('subSubIndustry', event.target.value)}
                    autoWidth={false}
                  >
                    {industryOptions.companySubSubIndustry.list.map(
                      (companySubSubIndustry, idx) => (
                        <StyledMenuItem
                          value={companySubSubIndustry.name}
                          key={`${companySubSubIndustry.name}_${idx}`}
                        >
                          {companySubSubIndustry.name}
                        </StyledMenuItem>
                      )
                    )}
                  </StyledInlineSelect>
                ) : null}
              </FormControl>
            </Grid>
          ) : null}
        </Grid>
        <Grid container spacing={24} alignContent="flex-start">
          <Grid item xs={12} sm={12} style={paddedBarWrapper}>
            <Grid container spacing={8}>
              <Grid item xs={12} sm={6}>
                <LabelStyle padding="0.7rem">Does your company have tipped employees?</LabelStyle>
              </Grid>
              <Grid item xs={12} sm={6}>
                <StyledSwitch
                  disabled={loading}
                  showValue
                  defaultChecked={company.hasTippedEmployees}
                  disableRipple
                  checked={company.hasTippedEmployees}
                  onChange={(event) => handleOnChange('hasTippedEmployees', event.currentTarget.checked)}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={24} alignContent="flex-start">
        <Grid item xs={12} sm={12} style={{ margin: '4rem 0' }}>
          <StyledButton
            color="primary"
            variant="contained"
            onClick={() => {
              setConfirmationModalOpen(true)
            }}
            disabled={loading}
          >
            Save
          </StyledButton>
          {loading && <CircularProgress size={24} style={{ marginLeft: '10px' }} />}
        </Grid>
      </Grid>
    </Grid>
  )
}

export default CompanySettingsRenderer
